import React, { Fragment, useEffect, useRef } from 'react'
import './App.css'
import { BrowserRouter } from 'react-router-dom'
import Routing from './Routing'
import { createTheme, ThemeProvider, Dialog, DialogTitle, DialogActions, DialogContent } from '@mui/material'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useLoginStore, MODAL_TIMEOUT } from 'src/Containers/Auth/Store/Store'
import { Button } from 'src/Common/ui/button'
import Axios from './Axios'
import Loading from './Common/ui/Loading'
import { OAUTH_ENABLED } from './Entities/AuthEntities'

function App() {
    const theme = createTheme({
        typography: {
            fontFamily: 'SF Pro'
        }
    })

    const { isAuthenticated, isModalOpen, resetInactivityTimer, logout, staySignedIn, refreshAccessToken, accessToken, startInactivityTimer } = useLoginStore()
    const logoutTimer = useRef(null)
    const debounceTimeout = useRef(null)
    const loading = useLoginStore(state => state.loading)
    const orgLoading = useLoginStore(state => state.orgLoading)

    // Start inactivity timer when user becomes authenticated
    useEffect(() => {
        if (!OAUTH_ENABLED) return
        if (isAuthenticated) {
            startInactivityTimer()
        }
    }, [isAuthenticated, startInactivityTimer])

    useEffect(() => {
        if (!OAUTH_ENABLED) return
        if (isAuthenticated) {
            // Debounced handler
            const debouncedHandleUserActivity = () => {
                if (debounceTimeout.current) clearTimeout(debounceTimeout.current)

                debounceTimeout.current = setTimeout(() => {
                    resetInactivityTimer()
                    clearTimeout(logoutTimer.current)
                }, 600) // 300ms debounce delay (adjust as needed)
            }

            const events = ['click', 'scroll', 'keypress', 'mousemove']
            events.forEach((event) =>
                document.addEventListener(event, debouncedHandleUserActivity)
            )

            return () => {
                events.forEach((event) =>
                    document.removeEventListener(event, debouncedHandleUserActivity)
                )
                clearTimeout(logoutTimer.current)
                clearTimeout(debounceTimeout.current)
            }
        }
    }, [isAuthenticated, resetInactivityTimer])

    useEffect(() => {
        if (OAUTH_ENABLED) {
            if (isAuthenticated && isModalOpen) {
                logoutTimer.current = setTimeout(() => {
                    logout()
                }, MODAL_TIMEOUT)
            } else {
                clearTimeout(logoutTimer.current)
            }

            return () => {
                clearTimeout(logoutTimer.current)
            }
        }
    }, [isAuthenticated, isModalOpen, logout])

    // Token refresh is now handled by Axios 401 interceptor (backend-driven)
    // No proactive frontend refresh timer needed

    useEffect(() => {
        if (!OAUTH_ENABLED) return

        const handleBeforeUnload = (event) => {
            if (isAuthenticated && isModalOpen) {
                logout()
            }
        }

        window.addEventListener('beforeunload', handleBeforeUnload)

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [isAuthenticated, isModalOpen, logout])

    useEffect(() => {
        if (!OAUTH_ENABLED) return
        Axios.setAccessToken(accessToken) // Force update token globally
    }, [accessToken])
    // In App.js useEffect for interceptors:
    useEffect(() => {
        const controller = new AbortController()

        // Cleanup on unmount
        return () => controller.abort()
    }, [])
    return (
        <Fragment>
            <Loading isLoading={loading || orgLoading} />
            {isAuthenticated && isModalOpen && OAUTH_ENABLED &&
                (
                    <Dialog open={isModalOpen} disableEscapeKeyDown>
                        <DialogTitle>Session Expiring Soon</DialogTitle>
                        <DialogContent>
                            <p>You have been inactive. Would you like to stay signed in?</p>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={logout} variant="outline">
                                Log Out
                            </Button>
                            <Button onClick={staySignedIn} variant="default" autoFocus>
                                Stay Signed In
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            <ThemeProvider theme={theme}>
                <BrowserRouter>
                    <Routing />
                    <ToastContainer
                        position="top-right"
                        hideProgressBar={true}
                        newestOnTop={false}
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        theme="light"
                        autoClose={false}
                    />
                </BrowserRouter>
            </ThemeProvider>
        </Fragment>
    )
}

export default App
